import React, { useState, useEffect } from "react";
import { 
  Box, Typography, Card, TextField, Grid, Button, IconButton, 
  Stack, InputAdornment, MenuItem, Select, FormControl, InputLabel 
} from "@mui/material";
import { 
  X, Save, Send, DollarSign, Calendar, Tag, FileText, Layout, ChevronLeft 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = { 
  ice: "#C2EDF0", 
  aqua: "#00C0CA", 
  teal: "#008B9C", 
  dark: "#22404D", 
  slate: "#94A3B8" 
};

export default function FormularioAdelanto() {
  const navigate = useNavigate();
  const [programas, setProgramas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [formValues, setFormValues] = useState({
    nombre: "",
    motivo: "",
    fecha: "",
    importe: "",
    moneda: "",
    centroDeCosto: "",
    idPrograma: "",
  });

  // Verificación de seguridad
  useEffect(() => {
    if (!localStorage.getItem("apiKey")) navigate("/");
  }, [navigate]);

  useEffect(() => {
    const rol = localStorage.getItem("rol");
    const fetchProgramas = async () => {
      try {
        let url = rol === "finanzas" 
          ? `${apiUrl}/Programa/get` 
          : `${apiUrl}/Usuarios/obtenerProgramasDeUsuario?mail=${localStorage.getItem("email")}`;

        const response = await fetch(url, {
          headers: { 'apiKey': localStorage.getItem("apiKey") }
        });

        if (response.ok) {
          const data = await response.json();
          setProgramas(data);
        }
      } catch (error) {
        console.error('Error fetching programas:', error);
      }
    };
    fetchProgramas();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validar = () => {
    const fechaFormulario = new Date(formValues.fecha);
    const fechaActual = new Date();
    fechaActual.setHours(0, 0, 0, 0);
    
    if (fechaFormulario < fechaActual) {
      toast.error("La fecha límite debe ser hoy o una fecha futura.");
      return true;
    }
    if (parseFloat(formValues.importe) <= 0) {
      toast.error("El monto debe ser mayor a 0.");
      return true;
    }
    return false;
  };

  const registrarAdelanto = async (e) => {
    e.preventDefault();
    if (validar()) return;

    // 1. Depuración: Verifica qué estás enviando antes de hacer el fetch
    console.log("Valores del formulario:", formValues);
    const apiKey = localStorage.getItem("apiKey");
    console.log("API Key recuperada:", apiKey);
    console.log(localStorage.getItem("apiKey"))
    setCargando(true);
    try {
      const registro = {
        ...formValues,
        id: 0,
        // Asegúrate de que idPrograma sea un número si el backend lo requiere
        idPrograma: Number(formValues.idPrograma), 
        centroCosto: formValues.centroDeCosto,
        estado: "Pendiente",
        email: localStorage.getItem("email"),
        usuarioId: localStorage.getItem("userId"),
        // Prueba agregar apiKey aquí si el header falla:
        // apiKey: apiKey 
      };

      const respuesta = await fetch(`${apiUrl}/Adelanto`, {
        method: 'POST',
        headers: {  apiKey: localStorage.getItem("apiKey"),
    "Content-Type": "application/json" },
        body: JSON.stringify(registro)
      });

      if (respuesta.ok) {
        toast.success('Adelanto solicitado con éxito');
        navigate(`/adelantos/${formValues.idPrograma}`);
      } else {
        // Aquí podrías llamar a tu función manejarErrores(respuesta.status)
        toast.error("Error al procesar la solicitud");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setCargando(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center', bgcolor: "#F8FAFB", minHeight: "100vh" }}>
      <Card 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 5 }, 
          borderRadius: '32px', 
          border: '1px solid #eee', 
          width: '100%', 
          maxWidth: 650, 
          bgcolor: 'white' 
        }}
      >
        {/* Cabecera */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark }}>Pedir Adelanto</Typography>
            <Typography variant="body2" sx={{ color: PALETTE.slate, fontWeight: 600 }}>Solicitud de fondos para actividades</Typography>
          </Box>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: PALETTE.ice }}>
            <X size={20} color={PALETTE.teal} />
          </IconButton>
        </Stack>

        <form onSubmit={registrarAdelanto}>
          <Grid container spacing={3}>
            {/* Actividad */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>ACTIVIDAD</Typography>
              <TextField 
                fullWidth 
                name="nombre"
                required
                value={formValues.nombre}
                onChange={handleChange}
                placeholder="Ej: Taller de capacitación" 
                InputProps={{ startAdornment: (<InputAdornment position="start"><Tag size={18} color={PALETTE.slate} /></InputAdornment>) }} 
                sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: '#FAFAFA' } }} 
              />
            </Grid>

            {/* Motivo */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>MOTIVO</Typography>
              <TextField 
                fullWidth 
                name="motivo"
                required
                value={formValues.motivo}
                onChange={handleChange}
                placeholder="¿Para qué se usará el dinero?" 
                InputProps={{ startAdornment: (<InputAdornment position="start"><FileText size={18} color={PALETTE.slate} /></InputAdornment>) }} 
                sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: '#FAFAFA' } }} 
              />
            </Grid>

            {/* Fecha */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FECHA LÍMITE</Typography>
              <TextField 
                fullWidth 
                type="date"
                name="fecha"
                required
                value={formValues.fecha}
                onChange={handleChange}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Calendar size={18} color={PALETTE.slate} /></InputAdornment>) }} 
                sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: '#FAFAFA' } }} 
              />
            </Grid>

            {/* Importe */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>IMPORTE</Typography>
              <TextField 
                fullWidth 
                type="number"
                name="importe"
                required
                value={formValues.importe}
                onChange={handleChange}
                InputProps={{ 
                    startAdornment: (<InputAdornment position="start"><DollarSign size={18} color={PALETTE.slate} /></InputAdornment>),
                }} 
                sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: '#FAFAFA' } }} 
              />
            </Grid>

            {/* Moneda y Programa */}
            <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>MONEDA</Typography>
                <TextField
                    select
                    fullWidth
                    name="moneda"
                    value={formValues.moneda}
                    onChange={handleChange}
                    required
                    sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: '#FAFAFA' } }}
                >
                    <MenuItem value="$">Peso ($)</MenuItem>
                    <MenuItem value="USD">Dólar (USD)</MenuItem>
                </TextField>
            </Grid>

            <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>PROGRAMA</Typography>
                <TextField
                    select
                    fullWidth
                    name="idPrograma"
                    value={formValues.idPrograma}
                    onChange={handleChange}
                    required
                    sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: '#FAFAFA' } }}
                >
                    {programas.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                    ))}
                </TextField>
            </Grid>
          </Grid>

          {/* Botones de Acción */}
          <Stack direction="row" spacing={2} mt={6}>
            <Button 
                onClick={() => navigate(-1)}
                fullWidth 
                variant="outlined" 
                sx={{ borderRadius: '16px', py: 1.8, fontWeight: 800, color: PALETTE.dark, borderColor: '#eee', '&:hover': { bgcolor: '#f9f9f9' } }}
            >
              Cancelar
            </Button>
            <Button 
                type="submit"
                disabled={cargando}
                fullWidth 
                variant="contained" 
                startIcon={<Send size={20} />} 
                sx={{ 
                    borderRadius: '16px', 
                    py: 1.8, 
                    fontWeight: 800, 
                    bgcolor: PALETTE.teal, 
                    fontSize: '1rem', 
                    '&:hover': { bgcolor: PALETTE.dark },
                    boxShadow: `0 8px 20px -4px ${PALETTE.teal}44`
                }}
            >
              {cargando ? "Enviando..." : "Solicitar Adelanto"}
            </Button>
          </Stack>
        </form>
      </Card>
    </Box>
  );
}